import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { copyText } from "@/lib/format";

/**
 * The bookmarklet as a javascript: URL. Built from the current origin so it
 * works in local dev (http://localhost:8787) and production (https://abe.lt).
 * Opens the create-page popup with the current tab's URL prefilled.
 *
 * Client-only: uses `window.location.origin` at render time. If this route is
 * ever SSR'd, build the origin from request headers / import.meta.env instead
 * and set `href` during SSR so the drag target is present on first paint.
 */
export function buildBookmarklet(origin: string): string {
	return `javascript:(()=>{var u=location.href;window.open('${origin}/links/new?url='+encodeURIComponent(u),'shorty','width=520,height=680');})()`;
}

export function BookmarkletPage() {
	const [copied, setCopied] = useState(false);
	// Vite SPA only — window is available; see buildBookmarklet JSDoc for SSR.
	const origin = window.location.origin;
	const href = buildBookmarklet(origin);

	// React blocks `javascript:` URLs in the href prop (security precaution).
	// Set the href imperatively on the DOM node so it isn't a React-whitelisted
	// attribute — required for the link to be draggable onto the bookmarks bar.
	// Until useEffect runs, the <a> has no href (one frame non-draggable). Fine
	// for CSR; under SSR the drag target would need href on the server HTML.
	const dragRef = useRef<HTMLAnchorElement>(null);
	useEffect(() => {
		if (dragRef.current) dragRef.current.href = href;
	}, [href]);

	async function onCopy() {
		const ok = await copyText(href);
		if (ok) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		}
	}

	return (
		<div className="mx-auto max-w-lg space-y-6">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">
					Bookmarklet
				</h1>
				<p className="mt-1 text-sm text-fg-muted">
					One-click shortening from any page.{" "}
					<Link to="/links" className="text-accent hover:underline">
						Back
					</Link>
				</p>
			</div>

			<div className="space-y-4 rounded-xl border border-border bg-surface-raised p-5">
				<div>
					<h2 className="text-sm font-medium">1. Drag to bookmarks bar</h2>
					<p className="mt-1 text-sm text-fg-muted">
						Drag the button below onto your browser's bookmarks bar.
					</p>
					<a
						ref={dragRef}
						className="mt-3 inline-block rounded-lg bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600"
					>
						Shorten this page
					</a>
				</div>

				<div>
					<h2 className="text-sm font-medium">2. Or copy the code</h2>
					<p className="mt-1 text-sm text-fg-muted">
						Create a bookmark with this as its URL/address.
					</p>
					<div className="mt-2 flex items-start gap-2">
						<code className="flex-1 break-all rounded-lg border border-border bg-surface px-3 py-2 font-mono text-xs text-fg">
							{href}
						</code>
						<button
							type="button"
							onClick={() => void onCopy()}
							className="shrink-0 rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface"
						>
							{copied ? "Copied!" : "Copy"}
						</button>
					</div>
				</div>

				<div>
					<h2 className="text-sm font-medium">How it works</h2>
					<ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-fg-muted">
						<li>
							Click it on any page → a popup opens with the current
							URL prefilled.
						</li>
						<li>
							If nothing happens, allow popups for this site — some
							browsers block <code className="font-mono text-xs">window.open</code>{" "}
							from bookmarklets when they do not treat the click as a
							user gesture.
						</li>
						<li>A short hash is suggested automatically — edit it if you like.</li>
						<li>Create → the short link is copied to your clipboard.</li>
						<li>If you're not logged in, you'll be asked to log in first.</li>
					</ul>
				</div>
			</div>
		</div>
	);
}
